import { Component, Input, OnInit, OnDestroy, ElementRef } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-roster-graph',
  templateUrl: './roster-graph.component.html',
  styleUrls: ['./roster-graph.component.scss']
})
export class RosterGraphComponent implements OnInit, OnDestroy {
  clarifier_low: number = 0;
  clarifier_high: number = 0;
  ideator_low: number = 0;
  ideator_high: number = 0;
  developer_low: number = 0;
  developer_high: number = 0;
  implementor_low: number = 0;
  implementor_high: number = 0;
  integrator: number = 0;
  highest_score: number = 0;
  total_members: number = 0;
  @Input() members;
  @Input() id;
  @Input() totalMem;
  profileValues: boolean = false;

  private destroy$ = new Subject<void>();
  private observer: IntersectionObserver;
  private loaded = false;

  constructor(public http: HttpClient, private el: ElementRef) {}

  ngOnInit() {
    if (this.totalMem == 0) {
      this.members = false;
      return;
    }

    if (this.id === '0000') return;

    // Lazy load: only fetch data when the card scrolls into view
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && !this.loaded) {
          this.loaded = true;
          this.fetchGraphData();
          this.observer.disconnect();
        }
      }, { threshold: 0.1 });
      this.observer.observe(this.el.nativeElement);
    } else {
      // Fallback for browsers without IntersectionObserver
      this.fetchGraphData();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  private fetchGraphData() {
    this.http.get(
      `${environment.profileURL}/api/v1/organizations/${this.id}/memberships.json?per_page=${this.totalMem}`
    ).pipe(takeUntil(this.destroy$)).subscribe(
      (data: any) => {
        this.members = data.memberships;
        this.total_members = this.members?.length;
        this.calculateScores();
      },
      () => {
        // On error, show empty graph skeleton instead of infinite loader
        this.members = false;
      }
    );
  }

  private calculateScores() {
    this.members?.forEach(list => {
      const profile = list?.default_profile;
      if (!profile) return;

      const updateScore = (scoreType: string, scoreValue: string) => {
        if (scoreValue === 'Low') {
          this[`${scoreType}_low`] = (this[`${scoreType}_low`] || 0) + 1;
          this.highest_score = Math.max(this.highest_score, this[`${scoreType}_low`]);
        } else if (scoreValue === 'High') {
          this[`${scoreType}_high`] = (this[`${scoreType}_high`] || 0) + 1;
          this.highest_score = Math.max(this.highest_score, this[`${scoreType}_high`]);
        }
      };

      updateScore('clarifier', profile.clarifier_score);
      updateScore('ideator', profile.ideator_score);
      updateScore('developer', profile.developer_score);
      updateScore('implementor', profile.implementer_score);

      if (profile.clarifier_score === 'Neutral' &&
        profile.ideator_score === 'Neutral' &&
        profile.developer_score === 'Neutral' &&
        profile.implementer_score === 'Neutral') {
        this.integrator = (this.integrator || 0) + 1;
      }
    });

    const resetScores = (scoreType: string) => {
      if (this[`${scoreType}_high`] === 0 && this[`${scoreType}_low`] === 0) {
        this[`${scoreType}_high`] = 50;
        this[`${scoreType}_low`] = 50;
      }
    };

    resetScores('clarifier');
    resetScores('ideator');
    resetScores('developer');
    resetScores('implementor');

    if (this.highest_score === 0) {
      this.highest_score = 50;
    }
  }
}
